import {
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter, Router, RouterOutlet, withHashLocation } from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import {
	AGILOX_API_ENDPOINT,
	App,
	APP_NAME,
	AppNavMenuPointsService,
	ChunkLoadingFailedErrorHandler,
	ENVIRONMENT,
	headersInterceptor,
	initializeTranslations,
	localeProvider,
	MENU_POINTS,
	MenuPointService,
	PLAUSIBLE_DOMAIN,
	PlausibleService,
	PluralTranslatePipe,
	provideDatepickerTranslationsWithNgxTranslate,
	provideSelectTranslationsWithNgxTranslate,
	SummaryOfTheYearService,
	ThemeService,
	TranslationsLoader,
	USER_FORMAT,
	provideFavicon,
} from '@agilox/common';
import {
	HttpClient,
	provideHttpClient,
	withInterceptors,
	withInterceptorsFromDi,
	withXhr,
} from '@angular/common/http';
import { NavModule } from '@agilox/ui';
import { AUTH_CONFIG, AuthModule, MSALAUTHConfigFactory, userInitializationFactory } from '@auth';
import { provideUserContext, UserStoreService } from '@user-store';
import { ToastService, UserNotificationsService } from '@agilox/components';
import { DatePipe, DecimalPipe } from '@angular/common';
import { CustomDatePipe } from '@agilox/pipe/custom-date';
import { CustomTimePipe } from '@agilox/pipe/custom-time';
import { CustomNumberPipe, provideNumberFormatter } from '@agilox/pipe/custom-number';
import { provideAnimations } from '@angular/platform-browser/animations';
import { environment } from '@lifecycle/env/environment';
import { desktopMenuPoints } from './general/shared/services/authentication/desktop-menupoints';
import { mobileMenuPoints } from './general/shared/services/authentication/mobile-menupoints';
import { MsalService } from '@azure/msal-angular';
import * as Sentry from '@sentry/angular';
import { createErrorHandlerInterceptor } from '@agilox/interceptors';
import { appRoutes } from './app.routes';

export const appConfig: ApplicationConfig = {
	providers: [
		provideFavicon(),
		importProvidersFrom(
			BrowserModule,
			RouterOutlet,
			TranslateModule.forRoot({
				loader: {
					provide: TranslateLoader,
					useClass: TranslationsLoader,
					deps: [HttpClient],
				},
			}),
			NavModule,
			AuthModule
		),
		provideHttpClient(
			withXhr(),
			withInterceptors([
				createErrorHandlerInterceptor({ app: App.lifecycle, environment, onError: 'propagate' }),
				headersInterceptor,
			]),
			withInterceptorsFromDi() // needed for MSAL
		),
		provideRouter(appRoutes, withHashLocation()),
		provideDatepickerTranslationsWithNgxTranslate(),
		provideSelectTranslationsWithNgxTranslate(),
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false,
			}),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		TranslateService,
		provideAppInitializer(() => {
			const initializerFn = initializeTranslations(inject(TranslateService));
			return initializerFn();
		}),
		{
			provide: ErrorHandler,
			useClass: ChunkLoadingFailedErrorHandler,
		},
		DatePipe,
		{
			provide: MENU_POINTS,
			useValue: {
				desktop: desktopMenuPoints,
				mobile: mobileMenuPoints,
			},
		},
		MenuPointService,
		provideAppInitializer(() => {
			const initializerFn = () => {
				const traceService = inject(Sentry.TraceService);
				return () => {};
			};
			return Promise.resolve(initializerFn());
		}),
		provideAppInitializer(() => {
			const userStore = inject(UserStoreService);
			const msalService = inject(MsalService);
			return userInitializationFactory(msalService, userStore)();
		}),
		provideAppInitializer(() => {
			inject(ThemeService);
		}),
		AppNavMenuPointsService,
		UserStoreService,
		...provideUserContext(),
		{
			provide: USER_FORMAT,
			useFactory: (userStore: UserStoreService) => userStore.userFormat,
			deps: [UserStoreService],
		},
		{
			provide: ENVIRONMENT,
			useValue: environment,
		},
		{
			provide: AGILOX_API_ENDPOINT,
			useValue: environment.server,
		},
		{
			provide: UserNotificationsService,
			deps: [AGILOX_API_ENDPOINT],
		},
		{
			provide: APP_NAME,
			useValue: App.lifecycle,
		},
		{
			provide: AUTH_CONFIG,
			useFactory: MSALAUTHConfigFactory,
			deps: [ENVIRONMENT],
		},
		{
			provide: PLAUSIBLE_DOMAIN,
			useValue: 'lifecycle.agilox.net',
		},
		PlausibleService,
		provideAppInitializer(() => {
			inject(PlausibleService);
		}),
		PluralTranslatePipe,
		localeProvider,
		DecimalPipe,
		CustomDatePipe,
		CustomTimePipe,
		CustomNumberPipe,
		...provideNumberFormatter(),
		ToastService,
		SummaryOfTheYearService,
		provideAnimations(),
	],
};
